import { useCallback } from 'react';
import { CategoryItem, DestinationItem, ResponsibleItem } from '../types/budget';
import { getBudget, updateBudget } from '../db';

export const useBudgetEntities = (budgetId: number | null) => {
  // Function to add a new category to a budget
  const addCategory = useCallback(
    (categoryName: string) => {
      if (!budgetId) return null;

      const budget = getBudget(budgetId);
      if (!budget) return null;

      // Check if category already exists
      const existingCategory = budget.categories.find(
        (cat) => cat.name.toLowerCase() === categoryName.toLowerCase()
      );

      if (existingCategory) {
        return existingCategory;
      }

      // Create new category
      const newCategory: CategoryItem = {
        id: categoryName.toLowerCase().replace(/\s+/g, '-'),
        name: categoryName,
      };

      // Add to budget
      budget.categories.push(newCategory);
      updateBudget(budget);

      return newCategory;
    },
    [budgetId]
  );

  // Function to add a new responsible to a budget
  const addResponsible = useCallback(
    (responsibleName: string) => {
      if (!budgetId) return null;

      const budget = getBudget(budgetId);
      if (!budget) return null;

      // Check if responsible already exists
      const existingResponsible = budget.responsibles.find(
        (resp) => resp.name.toLowerCase() === responsibleName.toLowerCase()
      );

      if (existingResponsible) {
        return existingResponsible;
      }

      // Create new responsible
      const newResponsible: ResponsibleItem = {
        id: responsibleName.toLowerCase().replace(/\s+/g, '-'),
        name: responsibleName,
      };

      // Add to budget
      budget.responsibles.push(newResponsible);
      updateBudget(budget);

      return newResponsible;
    },
    [budgetId]
  );

  // Function to add a new destination to a budget
  const addDestination = useCallback(
    (destinationName: string) => {
      if (!budgetId) return null;

      const budget = getBudget(budgetId);
      if (!budget) return null;

      // Check if destination already exists
      const existingDestination = budget.destinations.find(
        (dest) => dest.name.toLowerCase() === destinationName.toLowerCase()
      );

      if (existingDestination) {
        return existingDestination;
      }

      // Create new destination
      const newDestination: DestinationItem = {
        id: destinationName.toLowerCase().replace(/\s+/g, '-'),
        name: destinationName,
      };

      // Add to budget
      budget.destinations.push(newDestination);
      updateBudget(budget);

      return newDestination;
    },
    [budgetId]
  );

  // Function to update a category
  const updateCategory = useCallback(
    (categoryId: string, newName: string) => {
      if (!budgetId) return false;

      const budget = getBudget(budgetId);
      if (!budget) return false;

      const categoryIndex = budget.categories.findIndex((cat) => cat.id === categoryId);
      if (categoryIndex === -1) return false;

      // Update category name
      budget.categories[categoryIndex].name = newName;

      // Update all transactions using this category
      budget.transactions.forEach((transaction) => {
        if (transaction.category === budget.categories[categoryIndex].name) {
          transaction.category = newName;
        }
      });

      // Update the category name
      budget.categories[categoryIndex].name = newName;

      updateBudget(budget);
      return true;
    },
    [budgetId]
  );

  // Function to update a responsible
  const updateResponsible = useCallback(
    (responsibleId: string, newName: string) => {
      if (!budgetId) return false;

      const budget = getBudget(budgetId);
      if (!budget) return false;

      const responsibleIndex = budget.responsibles.findIndex((resp) => resp.id === responsibleId);
      if (responsibleIndex === -1) return false;

      // Check if this is a protected responsible (Common)
      if (budget.responsibles[responsibleIndex].isProtected) {
        return false;
      }

      // Update all transactions using this responsible
      budget.transactions.forEach((transaction) => {
        if (transaction.responsible === budget.responsibles[responsibleIndex].name) {
          transaction.responsible = newName;
        }
      });

      // Update the responsible name
      budget.responsibles[responsibleIndex].name = newName;

      updateBudget(budget);
      return true;
    },
    [budgetId]
  );

  // Function to update a destination
  const updateDestination = useCallback(
    (destinationId: string, newName: string) => {
      if (!budgetId) return false;

      const budget = getBudget(budgetId);
      if (!budget) return false;

      const destinationIndex = budget.destinations.findIndex((dest) => dest.id === destinationId);
      if (destinationIndex === -1) return false;

      // Check if this is a protected destination (Espèces)
      if (budget.destinations[destinationIndex].isProtected) {
        return false;
      }

      // Update all transactions using this destination
      budget.transactions.forEach((transaction) => {
        if (transaction.destination === budget.destinations[destinationIndex].name) {
          transaction.destination = newName;
        }
      });

      // Update the destination name
      budget.destinations[destinationIndex].name = newName;

      updateBudget(budget);
      return true;
    },
    [budgetId]
  );

  // Function to delete a category
  const deleteCategory = useCallback(
    (categoryId: string) => {
      if (!budgetId) return false;

      const budget = getBudget(budgetId);
      if (!budget) return false;

      // Check if the category is in use
      const isInUse = budget.transactions.some((tx) => {
        const category = budget.categories.find((cat) => cat.id === categoryId);
        return category && tx.category === category.name;
      });

      if (isInUse) {
        return false;
      }

      // Remove the category
      budget.categories = budget.categories.filter((cat) => cat.id !== categoryId);
      updateBudget(budget);
      return true;
    },
    [budgetId]
  );

  // Function to delete a responsible
  const deleteResponsible = useCallback(
    (responsibleId: string) => {
      if (!budgetId) return false;

      const budget = getBudget(budgetId);
      if (!budget) return false;

      // Check if this is the protected responsible (Common)
      const responsible = budget.responsibles.find((resp) => resp.id === responsibleId);
      if (responsible?.isProtected) {
        return false;
      }

      // Check if the responsible is in use
      const isInUse = budget.transactions.some((tx) => {
        const responsible = budget.responsibles.find((resp) => resp.id === responsibleId);
        return responsible && tx.responsible === responsible.name;
      });

      if (isInUse) {
        return false;
      }

      // Remove the responsible
      budget.responsibles = budget.responsibles.filter((resp) => resp.id !== responsibleId);
      updateBudget(budget);
      return true;
    },
    [budgetId]
  );

  // Function to delete a destination
  const deleteDestination = useCallback(
    (destinationId: string) => {
      if (!budgetId) return false;

      const budget = getBudget(budgetId);
      if (!budget) return false;

      // Check if this is the protected destination (Espèces)
      const destination = budget.destinations.find((dest) => dest.id === destinationId);
      if (destination?.isProtected) {
        return false;
      }

      // Check if the destination is in use
      const isInUse = budget.transactions.some((tx) => {
        const destination = budget.destinations.find((dest) => dest.id === destinationId);
        return destination && tx.destination === destination.name;
      });

      if (isInUse) {
        return false;
      }

      // Remove the destination
      budget.destinations = budget.destinations.filter((dest) => dest.id !== destinationId);
      updateBudget(budget);
      return true;
    },
    [budgetId]
  );

  return {
    addCategory,
    addResponsible,
    addDestination,
    updateCategory,
    updateResponsible,
    updateDestination,
    deleteCategory,
    deleteResponsible,
    deleteDestination,
  };
};
